import React, { useState } from "react";
import { BarChart3, Check, Lock, Plus, Trash2 } from "lucide-react";
import { Button } from "../shared/Button";
import { encuestas as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";
import { mostrarDia } from "../../utils/fecha";

/**
 * ENCUESTAS.
 *
 * Lo que hoy se decide en una asamblea a la que van doce personas de ciento
 * veinte. La pantalla existe para que votar cueste un toque desde el teléfono.
 *
 * **El resultado no se enseña antes de votar.** No es un detalle de diseño:
 * ver que una opción va ganando arrastra el voto, y una encuesta que se
 * arrastra sola no sirve para decidir nada. El backend tampoco lo manda —no
 * basta con no pintarlo—.
 */

const FORM_VACIO = { question: "", description: "", audience: "residentes", closesAt: "" };

/* El cierre es un día del calendario sin hora, y por eso va por `mostrarDia`
   y no por la zona de El Salvador: convertirlo restaba seis horas a una
   medianoche UTC y la encuesta decía que cerraba un día antes. */
const fecha = (f) => mostrarDia(f, { dateStyle: "medium" });

/** La barra de resultados. Sin librería de gráficas: es un `div` con ancho. */
function Barra({ opcion, resaltada }) {
  return (
    <div style={{ display: "grid", gap: "var(--space-1)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--text-sm)" }}>
        <span style={{ color: resaltada ? "var(--gold)" : "var(--text-secondary)", fontWeight: resaltada ? 600 : 400 }}>
          {resaltada ? "✓ " : ""}
          {opcion.opcion}
        </span>
        <span style={{ color: "var(--text-tertiary)" }}>
          {opcion.votos} · {opcion.porcentaje}%
        </span>
      </div>
      <div style={{ height: "8px", borderRadius: "4px", backgroundColor: "var(--bg-tertiary)", overflow: "hidden" }}>
        <div
          style={{
            width: `${opcion.porcentaje}%`,
            height: "100%",
            backgroundColor: resaltada ? "var(--gold)" : "var(--text-tertiary)",
            transition: "width .3s ease",
          }}
        />
      </div>
    </div>
  );
}

export default function Encuestas({ rol }) {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [opciones, setOpciones] = useState(["", ""]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.surveys || [];
  const esAdmin = rol === "admin";

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const votar = async (id, indice) => {
    setError("");
    try {
      await api.votar(id, { option: indice });
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo registrar el voto");
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    const limpias = opciones.map((o) => o.trim()).filter(Boolean);
    if (limpias.length < 2) {
      setError("Hacen falta al menos dos opciones");
      return;
    }
    setGuardando(true);
    try {
      await api.crear({ ...form, options: limpias, closesAt: form.closesAt || null });
      setAbierta(false);
      setForm(FORM_VACIO);
      setOpciones(["", ""]);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo crear");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Encuestas"
        subtitulo="Para decidir sin esperar a la próxima asamblea."
        accion={esAdmin ? "Nueva encuesta" : null}
        alAccionar={() => setAbierta(true)}
        icono={BarChart3}
      />

      {(error || errorCarga) && <Aviso>{error || errorCarga}</Aviso>}

      {cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>No hay encuestas abiertas.</Vacio>
      ) : (
        <div style={{ display: "grid", gap: "var(--space-4)" }}>
          {lista.map((e) => (
            <div
              key={e._id}
              style={{
                ...est.tarjeta,
                borderLeft: `4px solid ${e.status === "abierta" ? "var(--gold)" : "var(--text-tertiary)"}`,
              }}
            >
              <h3 style={est.tarjetaTitulo}>{e.question}</h3>
              {e.description && (
                <div style={{ ...est.tarjetaDato, display: "block", whiteSpace: "pre-wrap" }}>{e.description}</div>
              )}
              <div style={est.tarjetaDato}>
                {e.status === "abierta" ? "Abierta" : "Cerrada"}
                {e.closesAt ? ` · cierra el ${fecha(e.closesAt)}` : ""}
                {e.resultado ? ` · ${e.resultado.total} voto${e.resultado.total === 1 ? "" : "s"}` : ""}
              </div>

              {/* Con voto emitido o encuesta cerrada: los números.
                  Sin votar: sólo los botones. */}
              {e.resultado ? (
                <div style={{ display: "grid", gap: "var(--space-3)", marginTop: "var(--space-2)" }}>
                  {e.resultado.opciones.map((o) => (
                    <Barra key={o.indice} opcion={o} resaltada={e.miVoto === o.indice} />
                  ))}
                </div>
              ) : (
                <div style={est.tarjetaAcciones}>
                  {e.options.map((o, i) => (
                    <Button
                      key={i}
                      size="sm"
                      variant="outline"
                      disabled={!e.puedeVotar}
                      onClick={() => votar(e._id, i)}
                    >
                      {o.text}
                    </Button>
                  ))}
                </div>
              )}

              {/* Cambiar de opinión mientras siga abierta. El backend
                  sobrescribe el voto anterior, no lo suma. */}
              {e.resultado && e.puedeVotar && e.miVoto !== null && (
                <div style={est.tarjetaAcciones}>
                  {e.options.map((o, i) =>
                    i === e.miVoto ? null : (
                      <Button key={i} size="sm" variant="ghost" onClick={() => votar(e._id, i)}>
                        Cambiar a «{o.text}»
                      </Button>
                    )
                  )}
                </div>
              )}

              {esAdmin && e.status === "abierta" && (
                <div style={est.tarjetaAcciones}>
                  <Button size="sm" variant="ghost" leftIcon={Lock} onClick={() => api.cerrar(e._id).then(recargar)}>
                    Cerrar encuesta
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Nueva encuesta"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>
              Abrir encuesta
            </Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Pregunta" value={form.question} onChange={cambiar("question")} required />
        <Area etiqueta="Explicación (opcional)" value={form.description} onChange={cambiar("description")} />

        {opciones.map((o, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-2)" }}>
            <div style={{ flex: 1 }}>
              <Entrada
                etiqueta={`Opción ${i + 1}`}
                value={o}
                onChange={(ev) => setOpciones(opciones.map((x, j) => (j === i ? ev.target.value : x)))}
              />
            </div>
            {opciones.length > 2 && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={Trash2}
                onClick={() => setOpciones(opciones.filter((_, j) => j !== i))}
                aria-label={`Quitar la opción ${i + 1}`}
              />
            )}
          </div>
        ))}
        <Button size="sm" variant="outline" leftIcon={Plus} onClick={() => setOpciones([...opciones, ""])}>
          Agregar opción
        </Button>

        <div style={est.fila}>
          <Seleccion
            etiqueta="Para quién"
            value={form.audience}
            onChange={cambiar("audience")}
            opciones={[
              { valor: "residentes", etiqueta: "Residentes" },
              { valor: "todos", etiqueta: "Toda la residencial" },
              { valor: "administracion", etiqueta: "Sólo administración" },
            ]}
          />
          <Entrada etiqueta="Cierra el (opcional)" type="date" value={form.closesAt} onChange={cambiar("closesAt")} />
        </div>
      </Ventana>
    </div>
  );
}
